import React, { useState, useEffect } from 'react';
import {
    AiOutlinePlus,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineDownload,
    AiOutlinePrinter,
    AiOutlineDollar,
    AiOutlineShop,
    AiOutlineHome,
    AiOutlineWallet,
    AiOutlineEye,
    AiOutlineTeam,
    AiOutlineSwap,
    AiOutlineRise,
    AiOutlineFall,
    AiOutlineLineChart,
    AiOutlineCheckCircle,
    AiOutlineClockCircle,
    AiOutlineFileProtect,
    AiOutlineExclamationCircle,
} from 'react-icons/ai';
import styles from './Accounts.module.css';
import AxiosUrl from '../AxiosUrl';
import { useBusinessSettings } from '../../context/BusinessSettingsContext';
import { toast } from 'react-toastify';

function Accounts() {
    const { isFeatureEnabled } = useBusinessSettings();
    const uid = localStorage.getItem('id');
    const [activeTab, setActiveTab] = useState('business-investment');
    const [searchTerm, setSearchTerm] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [cashBalance, setCashBalance] = useState(0);
    const [cashBalanceLoading, setCashBalanceLoading] = useState(true);
    const [inventorySummary, setInventorySummary] = useState(null);
    const [inventoryLoading, setInventoryLoading] = useState(true);
    const [apSummary, setApSummary] = useState(null);
    const [apLoading, setApLoading] = useState(true);
    const [arSummary, setArSummary] = useState(null);
    const [arLoading, setArLoading] = useState(true);
    const [partnersSummary, setPartnersSummary] = useState(null);
    const [partnersLoading, setPartnersLoading] = useState(true);
    const [partnerTransactions, setPartnerTransactions] = useState(null);
    const [partnerTransactionsLoading, setPartnerTransactionsLoading] = useState(true);
    const [profitDistributions, setProfitDistributions] = useState(null);
    const [profitDistributionsLoading, setProfitDistributionsLoading] = useState(true);
    const [approvals, setApprovals] = useState(null);
    const [approvalsLoading, setApprovalsLoading] = useState(true);

    useEffect(() => {
        if (isFeatureEnabled('enableCashFlow')) {
            fetchCashBalance();
            fetchAccountsPayableSummary();
            fetchAccountsReceivableSummary();
        }
        if (isFeatureEnabled('enableInventory')) {
            fetchInventorySummary();
        }
        if (isFeatureEnabled('enablePartners')) {
            fetchPartnersSummary();
            fetchPartnerTransactions();
            fetchProfitDistributions();
        }
        if (isFeatureEnabled('enableApproval')) {
            fetchApprovals();
        }
    }, [isFeatureEnabled]);

    const fetchCashBalance = async () => {
        try {
            setCashBalanceLoading(true);
            const response = await AxiosUrl.get(`/cash-balance/${uid}`);
            if (response.data.success) {
                setCashBalance(response.data.data.currentBalance);
            }
        } catch (error) {
            console.error('Error fetching cash balance:', error);
            toast.error('Failed to fetch cash balance');
        } finally {
            setCashBalanceLoading(false);
        }
    };

    const fetchInventorySummary = async () => {
        try {
            setInventoryLoading(true);
            const response = await AxiosUrl.get(`/inventory-summary/${uid}`);
            if (response.data.success) {
                setInventorySummary(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching inventory summary:', error);
            toast.error('Failed to fetch inventory summary');
        } finally {
            setInventoryLoading(false);
        }
    };

    const fetchAccountsPayableSummary = async () => {
        try {
            setApLoading(true);
            const response = await AxiosUrl.get(`/accounts-payable-summary/${uid}`);
            if (response.data.success) {
                setApSummary(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching accounts payable summary:', error);
            toast.error('Failed to fetch accounts payable summary');
        } finally {
            setApLoading(false);
        }
    };

    const fetchAccountsReceivableSummary = async () => {
        try {
            setArLoading(true);
            const response = await AxiosUrl.get(`/accounts-receivable-summary/${uid}`);
            if (response.data.success) {
                setArSummary(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching accounts receivable summary:', error);
            toast.error('Failed to fetch accounts receivable summary');
        } finally {
            setArLoading(false);
        }
    };

    const fetchPartnersSummary = async () => {
        try {
            setPartnersLoading(true);
            const response = await AxiosUrl.get(`/partners/${uid}`);
            if (response.data.success) {
                setPartnersSummary(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching partners summary:', error);
            toast.error('Failed to fetch partners summary');
        } finally {
            setPartnersLoading(false);
        }
    };

    const fetchPartnerTransactions = async () => {
        try {
            setPartnerTransactionsLoading(true);
            const response = await AxiosUrl.get(`/partner-transactions/${uid}?limit=10`);
            if (response.data.success) {
                setPartnerTransactions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching partner transactions:', error);
            toast.error('Failed to fetch partner transactions');
        } finally {
            setPartnerTransactionsLoading(false);
        }
    };

    const fetchProfitDistributions = async () => {
        try {
            setProfitDistributionsLoading(true);
            const response = await AxiosUrl.get(`/profit-distributions/${uid}?limit=5`);
            if (response.data.success) {
                setProfitDistributions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching profit distributions:', error);
            toast.error('Failed to fetch profit distributions');
        } finally {
            setProfitDistributionsLoading(false);
        }
    };

    const fetchApprovals = async () => {
        try {
            setApprovalsLoading(true);
            const response = await AxiosUrl.get(`/approvals/${uid}?status=pending&limit=10`);
            if (response.data.success) {
                setApprovals(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching approvals:', error);
            toast.error('Failed to fetch approvals');
        } finally {
            setApprovalsLoading(false);
        }
    };

    // Sample data - replace with actual API calls
    const businessInvestments = [
        {
            id: 1,
            date: '2024-01-15',
            name: 'Ruhul Amin',
            amount: 2000000,
            type: 'Investment',
        },
        {
            id: 2,
            date: '2024-01-10',
            name: 'Dr Farid Uddin',
            amount: 2000000,
            type: 'Investment',
        },
    ];

    const fixedAssets = [
        {
            id: 1,
            date: '2024-01-15',
            name: 'Purchase Server',
            amount: 500000,
            type: 'Asset',
        },
        {
            id: 2,
            date: '2024-01-10',
            name: 'Office Advance',
            amount: 100000,
            type: 'Asset',
        },
    ];

    const currentAssets = [
        { name: 'Fixed Asset', amount: 3253647 },
        { name: 'Inventory', amount: 3575688 },
        { name: 'Available', amount: 500000 },
    ];

    const formatCurrency = (amount) => {
        return `৳${amount.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getTotalAmount = (items) => {
        return items.reduce((sum, item) => sum + item.amount, 0);
    };

    const tabs = [
        {
            id: 'business-investment',
            label: 'Business Investment',
            icon: AiOutlineShop,
        },
        { id: 'fixed-assets', label: 'Fixed Assets', icon: AiOutlineHome },
        {
            id: 'current-assets',
            label: 'Current Assets',
            icon: AiOutlineWallet,
        },
    ];

    const getCurrentData = () => {
        switch (activeTab) {
            case 'business-investment':
                return businessInvestments;
            case 'fixed-assets':
                return fixedAssets;
            case 'current-assets':
                return currentAssets;
            default:
                return [];
        }
    };

    const filteredData = getCurrentData().filter((item) => {
        if (activeTab === 'current-assets') {
            return item.name.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return (
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.type.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    return (
        <div className={styles.accountsContainer}>
            {/* Header Section */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Accounts Management</h1>
                    <p className={styles.pageSubtitle}>
                        Manage your business investments, assets, and financial
                        records
                    </p>
                </div>
                <div className={styles.headerRight}>
                    <button
                        className={styles.primaryButton}
                        onClick={() => console.log('Add Record')}
                    >
                        <AiOutlinePlus className={styles.buttonIcon} />
                        Add Record
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineShop />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {formatCurrency(
                                getTotalAmount(businessInvestments)
                            )}
                        </div>
                        <div className={styles.statLabel}>
                            Business Investment
                        </div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineHome />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {formatCurrency(getTotalAmount(fixedAssets))}
                        </div>
                        <div className={styles.statLabel}>Fixed Assets</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineWallet />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {formatCurrency(getTotalAmount(currentAssets))}
                        </div>
                        <div className={styles.statLabel}>Current Assets</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                        <AiOutlineDollar />
                    </div>
                    <div className={styles.statContent}>
                        <div className={styles.statValue}>
                            {formatCurrency(
                                getTotalAmount(businessInvestments) +
                                    getTotalAmount(fixedAssets) +
                                    getTotalAmount(currentAssets)
                            )}
                        </div>
                        <div className={styles.statLabel}>Total Assets</div>
                    </div>
                </div>
            </div>

            {/* Cash Balance Widget (Phase 3) */}
            {isFeatureEnabled('enableCashFlow') && (
                <div className={styles.cashBalanceWidget}>
                    <div className={styles.cashBalanceCard}>
                        <div className={styles.cashBalanceIcon}>
                            <AiOutlineDollar />
                        </div>
                        <div className={styles.cashBalanceContent}>
                            <div className={styles.cashBalanceLabel}>
                                Current Cash Balance
                            </div>
                            <div className={styles.cashBalanceValue}>
                                {cashBalanceLoading
                                    ? 'Loading...'
                                    : formatCurrency(cashBalance)}
                            </div>
                            <div className={styles.cashBalanceDescription}>
                                Track your cash movements from expenses and purchases
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Inventory Widget (Phase 4) */}
            {isFeatureEnabled('enableInventory') && (
                <div className={styles.inventoryWidget}>
                    <div className={styles.inventoryCard}>
                        <div className={styles.inventoryIcon}>
                            <AiOutlineShop />
                        </div>
                        <div className={styles.inventoryContent}>
                            <div className={styles.inventoryLabel}>
                                Inventory Summary
                            </div>
                            {inventoryLoading ? (
                                <div className={styles.inventoryValue}>
                                    Loading...
                                </div>
                            ) : inventorySummary?.enabled ? (
                                <>
                                    <div className={styles.inventoryStats}>
                                        <div className={styles.inventoryStat}>
                                            <div
                                                className={
                                                    styles.inventoryStatValue
                                                }
                                            >
                                                {formatCurrency(
                                                    inventorySummary.totalValue ||
                                                        0
                                                )}
                                            </div>
                                            <div
                                                className={
                                                    styles.inventoryStatLabel
                                                }
                                            >
                                                Total Value
                                            </div>
                                        </div>
                                        <div className={styles.inventoryStat}>
                                            <div
                                                className={
                                                    styles.inventoryStatValue
                                                }
                                            >
                                                {inventorySummary.totalProducts ||
                                                    0}
                                            </div>
                                            <div
                                                className={
                                                    styles.inventoryStatLabel
                                                }
                                            >
                                                Products
                                            </div>
                                        </div>
                                        <div className={styles.inventoryStat}>
                                            <div
                                                className={`${
                                                    styles.inventoryStatValue
                                                } ${
                                                    inventorySummary.lowStockCount >
                                                    0
                                                        ? styles.inventoryWarning
                                                        : ''
                                                }`}
                                            >
                                                {inventorySummary.lowStockCount ||
                                                    0}
                                            </div>
                                            <div
                                                className={
                                                    styles.inventoryStatLabel
                                                }
                                            >
                                                Low Stock
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.inventoryDescription}>
                                        Real-time inventory tracking from purchases
                                        and sales
                                    </div>
                                </>
                            ) : (
                                <div className={styles.inventoryDescription}>
                                    No inventory data available
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Accounts Payable & Receivable (Phase 5) */}
            {isFeatureEnabled('enableCashFlow') && (
                <div className={styles.apArContainer}>
                    {/* Accounts Payable Widget */}
                    <div className={styles.apCard}>
                        <div className={styles.apHeader}>
                            <AiOutlineDollar className={styles.apIcon} />
                            <div>
                                <div className={styles.apLabel}>
                                    Accounts Payable
                                </div>
                                <div className={styles.apSubLabel}>
                                    Money we owe to suppliers
                                </div>
                            </div>
                        </div>
                        {apLoading ? (
                            <div className={styles.apValue}>Loading...</div>
                        ) : apSummary?.enabled ? (
                            <>
                                <div className={styles.apValue}>
                                    {formatCurrency(
                                        apSummary.totalPayable || 0
                                    )}
                                </div>
                                <div className={styles.apStats}>
                                    <div className={styles.apStat}>
                                        <span
                                            className={
                                                apSummary.overdueCount > 0
                                                    ? styles.apOverdue
                                                    : ''
                                            }
                                        >
                                            {apSummary.overdueCount || 0}
                                        </span>{' '}
                                        Overdue
                                    </div>
                                    <div className={styles.apStat}>
                                        <span>{apSummary.upcomingCount || 0}</span>{' '}
                                        Due in 7 days
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className={styles.apValue}>৳0</div>
                        )}
                    </div>

                    {/* Accounts Receivable Widget */}
                    <div className={styles.arCard}>
                        <div className={styles.arHeader}>
                            <AiOutlineDollar className={styles.arIcon} />
                            <div>
                                <div className={styles.arLabel}>
                                    Accounts Receivable
                                </div>
                                <div className={styles.arSubLabel}>
                                    Money customers owe us
                                </div>
                            </div>
                        </div>
                        {arLoading ? (
                            <div className={styles.arValue}>Loading...</div>
                        ) : arSummary?.enabled ? (
                            <>
                                <div className={styles.arValue}>
                                    {formatCurrency(
                                        arSummary.totalReceivable || 0
                                    )}
                                </div>
                                <div className={styles.arStats}>
                                    <div className={styles.arStat}>
                                        <span
                                            className={
                                                arSummary.overdueCount > 0
                                                    ? styles.arOverdue
                                                    : ''
                                            }
                                        >
                                            {arSummary.overdueCount || 0}
                                        </span>{' '}
                                        Overdue
                                    </div>
                                    <div className={styles.arStat}>
                                        <span>{arSummary.upcomingCount || 0}</span>{' '}
                                        Due in 7 days
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className={styles.arValue}>৳0</div>
                        )}
                    </div>
                </div>
            )}

            {/* Partners Widget (Phase 6) */}
            {isFeatureEnabled('enablePartners') && (
                <div className={styles.partnersWidget}>
                    <div className={styles.partnersCard}>
                        <div className={styles.partnersHeader}>
                            <AiOutlineTeam className={styles.partnersIcon} />
                            <div>
                                <div className={styles.partnersLabel}>
                                    Business Partners
                                </div>
                                <div className={styles.partnersSubLabel}>
                                    Ownership and capital tracking
                                </div>
                            </div>
                        </div>
                        {partnersLoading ? (
                            <div className={styles.partnersValue}>Loading...</div>
                        ) : partnersSummary?.enabled ? (
                            <>
                                <div className={styles.partnersStats}>
                                    <div className={styles.partnersStat}>
                                        <div className={styles.partnersStatValue}>
                                            {partnersSummary.summary?.activePartners || 0}
                                        </div>
                                        <div className={styles.partnersStatLabel}>
                                            Active Partners
                                        </div>
                                    </div>
                                    <div className={styles.partnersStat}>
                                        <div className={styles.partnersStatValue}>
                                            {partnersSummary.summary?.totalOwnership?.toFixed(1) || 0}%
                                        </div>
                                        <div className={styles.partnersStatLabel}>
                                            Total Ownership
                                        </div>
                                    </div>
                                    <div className={styles.partnersStat}>
                                        <div className={styles.partnersStatValue}>
                                            {formatCurrency(
                                                partnersSummary.summary?.totalCapital || 0
                                            )}
                                        </div>
                                        <div className={styles.partnersStatLabel}>
                                            Total Capital
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.partnersDescription}>
                                    Manage multiple business owners and track capital accounts
                                </div>
                            </>
                        ) : (
                            <div className={styles.partnersDescription}>
                                No partners added yet
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Partner Capital Transactions Widget (Phase 7) */}
            {isFeatureEnabled('enablePartners') && (
                <div className={styles.partnerTransactionsWidget}>
                    <div className={styles.partnerTransactionsCard}>
                        <div className={styles.partnerTransactionsHeader}>
                            <AiOutlineSwap className={styles.partnerTransactionsIcon} />
                            <div>
                                <div className={styles.partnerTransactionsLabel}>
                                    Partner Capital Transactions
                                </div>
                                <div className={styles.partnerTransactionsSubLabel}>
                                    Track investments, drawings, and profit distributions
                                </div>
                            </div>
                        </div>
                        {partnerTransactionsLoading ? (
                            <div className={styles.partnerTransactionsValue}>Loading...</div>
                        ) : partnerTransactions?.summary ? (
                            <>
                                <div className={styles.partnerTransactionsStats}>
                                    <div className={styles.partnerTransactionsStat}>
                                        <div className={styles.partnerTransactionsStatIcon}>
                                            <AiOutlineRise />
                                        </div>
                                        <div>
                                            <div className={styles.partnerTransactionsStatValue}>
                                                {formatCurrency(
                                                    partnerTransactions.summary?.totalInvestments || 0
                                                )}
                                            </div>
                                            <div className={styles.partnerTransactionsStatLabel}>
                                                Total Investments
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.partnerTransactionsStat}>
                                        <div className={styles.partnerTransactionsStatIcon}>
                                            <AiOutlineFall />
                                        </div>
                                        <div>
                                            <div className={styles.partnerTransactionsStatValue}>
                                                {formatCurrency(
                                                    partnerTransactions.summary?.totalDrawings || 0
                                                )}
                                            </div>
                                            <div className={styles.partnerTransactionsStatLabel}>
                                                Total Drawings
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.partnerTransactionsStat}>
                                        <div className={styles.partnerTransactionsStatIcon}>
                                            <AiOutlineDollar />
                                        </div>
                                        <div>
                                            <div className={styles.partnerTransactionsStatValue}>
                                                {formatCurrency(
                                                    partnerTransactions.summary?.totalProfitDistributions || 0
                                                )}
                                            </div>
                                            <div className={styles.partnerTransactionsStatLabel}>
                                                Profit Distributions
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.partnerTransactionsDescription}>
                                    {partnerTransactions.summary?.transactionCount || 0} transactions recorded
                                </div>
                            </>
                        ) : (
                            <div className={styles.partnerTransactionsDescription}>
                                No transactions recorded yet
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Profit Distribution Widget (Phase 8) */}
            {isFeatureEnabled('enablePartners') && (
                <div className={styles.profitDistributionWidget}>
                    <div className={styles.profitDistributionCard}>
                        <div className={styles.profitDistributionHeader}>
                            <AiOutlineLineChart className={styles.profitDistributionIcon} />
                            <div>
                                <div className={styles.profitDistributionLabel}>
                                    Profit Distribution
                                </div>
                                <div className={styles.profitDistributionSubLabel}>
                                    Calculate and distribute profits to partners
                                </div>
                            </div>
                        </div>
                        {profitDistributionsLoading ? (
                            <div className={styles.profitDistributionValue}>Loading...</div>
                        ) : profitDistributions?.summary ? (
                            <>
                                <div className={styles.profitDistributionStats}>
                                    <div className={styles.profitDistributionStat}>
                                        <div className={styles.profitDistributionStatIcon}>
                                            <AiOutlineCheckCircle />
                                        </div>
                                        <div>
                                            <div className={styles.profitDistributionStatValue}>
                                                {formatCurrency(
                                                    profitDistributions.summary?.totalProfitDistributed || 0
                                                )}
                                            </div>
                                            <div className={styles.profitDistributionStatLabel}>
                                                Total Distributed
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.profitDistributionStat}>
                                        <div className={styles.profitDistributionStatIcon}>
                                            <AiOutlineClockCircle />
                                        </div>
                                        <div>
                                            <div className={styles.profitDistributionStatValue}>
                                                {formatCurrency(
                                                    profitDistributions.summary?.undistributedProfit || 0
                                                )}
                                            </div>
                                            <div className={styles.profitDistributionStatLabel}>
                                                Undistributed Profit
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.profitDistributionStat}>
                                        <div className={styles.profitDistributionStatIcon}>
                                            <AiOutlineDollar />
                                        </div>
                                        <div>
                                            <div className={styles.profitDistributionStatValue}>
                                                {profitDistributions.summary?.totalDistributed || 0}
                                            </div>
                                            <div className={styles.profitDistributionStatLabel}>
                                                Distributions Made
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.profitDistributionDescription}>
                                    {profitDistributions.summary?.totalCalculated || 0} calculated, {profitDistributions.summary?.totalDistributed || 0} distributed
                                </div>
                            </>
                        ) : (
                            <div className={styles.profitDistributionDescription}>
                                No profit distributions yet
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Approvals Widget (Phase 10) */}
            {isFeatureEnabled('enableApproval') && (
                <div className={styles.approvalsWidget}>
                    <div className={styles.approvalsCard}>
                        <div className={styles.approvalsHeader}>
                            <AiOutlineFileProtect className={styles.approvalsIcon} />
                            <div>
                                <div className={styles.approvalsLabel}>
                                    Pending Approvals
                                </div>
                                <div className={styles.approvalsSubLabel}>
                                    Transactions awaiting approval
                                </div>
                            </div>
                        </div>
                        {approvalsLoading ? (
                            <div className={styles.approvalsValue}>Loading...</div>
                        ) : approvals?.summary ? (
                            <>
                                <div className={styles.approvalsStats}>
                                    <div className={styles.approvalsStat}>
                                        <div className={styles.approvalsStatIcon}>
                                            <AiOutlineClockCircle />
                                        </div>
                                        <div>
                                            <div className={styles.approvalsStatValue}>
                                                {approvals.summary?.pendingRequests || 0}
                                            </div>
                                            <div className={styles.approvalsStatLabel}>
                                                Pending Requests
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.approvalsStat}>
                                        <div className={styles.approvalsStatIcon}>
                                            <AiOutlineDollar />
                                        </div>
                                        <div>
                                            <div className={styles.approvalsStatValue}>
                                                {formatCurrency(
                                                    approvals.summary?.totalPendingAmount || 0
                                                )}
                                            </div>
                                            <div className={styles.approvalsStatLabel}>
                                                Pending Amount
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.approvalsStat}>
                                        <div className={styles.approvalsStatIcon}>
                                            <AiOutlineCheckCircle />
                                        </div>
                                        <div>
                                            <div className={styles.approvalsStatValue}>
                                                {approvals.summary?.approvedRequests || 0}
                                            </div>
                                            <div className={styles.approvalsStatLabel}>
                                                Approved Requests
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {approvals.summary?.pendingRequests > 0 && (
                                    <div className={styles.approvalsAlert}>
                                        <AiOutlineExclamationCircle className={styles.approvalsAlertIcon} />
                                        {approvals.summary.pendingRequests} transaction{approvals.summary.pendingRequests > 1 ? 's' : ''} require{approvals.summary.pendingRequests > 1 ? '' : 's'} your approval
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.approvalsDescription}>
                                No pending approvals
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tabs Navigation */}
            <div className={styles.tabsContainer}>
                <div className={styles.tabsHeader}>
                    <div className={styles.tabsList}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                className={`${styles.tabButton} ${
                                    activeTab === tab.id ? styles.tabActive : ''
                                }`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <tab.icon className={styles.tabIcon} />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className={styles.filtersSection}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Search records..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                    <AiOutlineSearch className={styles.searchIcon} />
                </div>
                <div className={styles.filterButtons}>
                    <button className={styles.actionButton}>
                        <AiOutlineDownload className={styles.actionIcon} />
                        Export
                    </button>
                    <button className={styles.actionButton}>
                        <AiOutlinePrinter className={styles.actionIcon} />
                        Print
                    </button>
                </div>
            </div>

            {/* Content Section */}
            <div className={styles.contentSection}>
                {activeTab === 'current-assets' ? (
                    /* Current Assets Grid */
                    <div className={styles.assetsGrid}>
                        {filteredData.map((asset, index) => (
                            <div key={index} className={styles.assetCard}>
                                <div className={styles.assetIcon}>
                                    <AiOutlineWallet />
                                </div>
                                <div className={styles.assetContent}>
                                    <div className={styles.assetName}>
                                        {asset.name}
                                    </div>
                                    <div className={styles.assetAmount}>
                                        {formatCurrency(asset.amount)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* Table for Business Investment and Fixed Assets */
                    <div className={styles.tableContainer}>
                        <div className={styles.tableHeader}>
                            <div className={styles.tableTitle}>
                                {activeTab === 'business-investment'
                                    ? 'Business Investments'
                                    : 'Fixed Assets'}
                            </div>
                            <div className={styles.tableActions}>
                                <button
                                    className={styles.addButton}
                                    onClick={() => console.log('Add Record')}
                                >
                                    <AiOutlinePlus
                                        className={styles.actionIcon}
                                    />
                                    Add{' '}
                                    {activeTab === 'business-investment'
                                        ? 'Investment'
                                        : 'Asset'}
                                </button>
                            </div>
                        </div>

                        <div className={styles.tableWrapper}>
                            <table className={styles.accountsTable}>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={styles.tableRow}
                                        >
                                            <td className={styles.dateCell}>
                                                <div
                                                    className={styles.dateValue}
                                                >
                                                    {formatDate(item.date)}
                                                </div>
                                            </td>
                                            <td className={styles.nameCell}>
                                                <div
                                                    className={styles.nameValue}
                                                >
                                                    {item.name}
                                                </div>
                                            </td>
                                            <td className={styles.typeCell}>
                                                <span
                                                    className={styles.typeBadge}
                                                >
                                                    {item.type}
                                                </span>
                                            </td>
                                            <td className={styles.amountCell}>
                                                <div
                                                    className={
                                                        styles.amountValue
                                                    }
                                                >
                                                    {formatCurrency(
                                                        item.amount
                                                    )}
                                                </div>
                                            </td>
                                            <td className={styles.actionsCell}>
                                                <div
                                                    className={
                                                        styles.actionButtons
                                                    }
                                                >
                                                    <button
                                                        className={
                                                            styles.editButton
                                                        }
                                                        onClick={() =>
                                                            setEditingItem(item)
                                                        }
                                                        title="Edit"
                                                    >
                                                        <AiOutlineEdit />
                                                    </button>
                                                    <button
                                                        className={
                                                            styles.viewButton
                                                        }
                                                        title="View Details"
                                                    >
                                                        <AiOutlineEye />
                                                    </button>
                                                    <button
                                                        className={
                                                            styles.deleteButton
                                                        }
                                                        title="Delete"
                                                    >
                                                        <AiOutlineDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary Row */}
                        <div className={styles.summaryRow}>
                            <div className={styles.summaryContent}>
                                <div className={styles.summaryLabel}>
                                    Total{' '}
                                    {activeTab === 'business-investment'
                                        ? 'Investment'
                                        : 'Assets'}
                                </div>
                                <div className={styles.summaryAmount}>
                                    {formatCurrency(
                                        getTotalAmount(filteredData)
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Empty State */}
            {filteredData.length === 0 && (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <AiOutlineWallet />
                    </div>
                    <h3 className={styles.emptyTitle}>No records found</h3>
                    <p className={styles.emptyDescription}>
                        {searchTerm
                            ? 'Try adjusting your search criteria'
                            : `Get started by adding your first ${
                                  activeTab === 'business-investment'
                                      ? 'investment'
                                      : activeTab === 'fixed-assets'
                                      ? 'asset'
                                      : 'record'
                              }`}
                    </p>
                    {!searchTerm && (
                        <button
                            className={styles.emptyActionButton}
                            onClick={() => setShowAddModal(true)}
                        >
                            Add Record
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Accounts;
